import { useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useGridApiContext, type GridColSpanFn, type GridRowId } from '@mui/x-data-grid-premium';
import { ContractsByMissionGQL, Role, TemplateContract } from '@bloomays-lib/types.shared';
import { FileViewer, LoaderPending, Modal, resolveFileRequest } from '@bloomays-lib/ui.shared';
import { DataGridV2 } from '@bloomays-lib/ui.shared/datagrid';
import type { DataGridV2Column, DataGridV2QuickFilter } from '@bloomays-lib/ui.shared/datagrid';
import type { ContractSegment } from '@bloomays-lib/utils.shared';
import { useTranslation } from 'react-i18next';
import {
  buildMissionContractGroups,
  flattenMissionContractGroups,
  type FilterOption,
  isVoletVisibleUnderSigningFilter,
  MissionContractBlock,
  MissionContractGridRow,
  type SigningStateFilter,
} from '../../helpers/legalContracts';
import MissionGroupHeader from '../molecules/MissionGroupHeader';
import DocumentVoletCard from '../molecules/DocumentVoletCard';
import { useAuthStore } from '../../stores';
import { notify } from '../../helpers/toastify';
import { downloadBlob } from '../../helpers/downloadBlob';

/** Reuses `FileViewer`'s URL resolution, then triggers a browser download. */
const downloadSignedFile = async (url: string, t: (key: string) => string, accessToken?: string): Promise<void> => {
  const { requestUrl, requiresAuth } = resolveFileRequest(url);
  if (requiresAuth && !accessToken) {
    notify('error', t('downloadError'));
    return;
  }

  try {
    const response = requiresAuth
      ? await fetch(requestUrl, { headers: { Authorization: `Bearer ${accessToken}` } })
      : await fetch(requestUrl);

    if (!response.ok) {
      notify('error', t('downloadError'));
      return;
    }

    const blob = await response.blob();
    downloadBlob(blob, 'document-signe.pdf');
  } catch {
    notify('error', t('downloadError'));
  }
};

type MissionGroupCellProps = {
  block: MissionContractBlock;
  rowId: GridRowId;
  childrenExpanded: boolean;
};

/**
 * Resolves the grid `apiRef` via context (the MUI-recommended pattern for a
 * custom grouping cell, cf. `GridTreeDataGroupingCell`) rather than threading
 * an external `apiRef` prop through `DataGridV2`, whose typing expects a
 * non-nullable `MutableRefObject` incompatible with `useGridApiRef()`'s actual
 * (nullable) return type in this MUI version.
 */
const MissionGroupCell = ({ block, rowId, childrenExpanded }: MissionGroupCellProps) => {
  const apiRef = useGridApiContext();
  return (
    <MissionGroupHeader
      block={block}
      expanded={childrenExpanded}
      onToggleExpanded={() => apiRef.current.setRowChildrenExpansion(rowId, !childrenExpanded)}
    />
  );
};

/** MUI auto-generated id prefix for row-grouping header rows. */
const GROUP_ROW_ID_PREFIX = 'auto-generated-row';

/** Visible columns in grouped layout: grouping + SST + PDS. */
const GROUPED_CONTRACTS_COL_SPAN = 3;

const resolveGroupedContractsColSpan: GridColSpanFn<MissionContractGridRow> = (_value, row, _column, apiRef) => {
  if (!row || !apiRef.current) return 1;
  const rowId = String(apiRef.current.getRowId(row));
  if (rowId.startsWith(GROUP_ROW_ID_PREFIX)) {
    return apiRef.current.getVisibleColumns().length || GROUPED_CONTRACTS_COL_SPAN;
  }
  return 1;
};

/** Valeur du quick-filter « À signer / Signés » (`''` = tous). */
export type SigningStateQuickFilter = SigningStateFilter;

export type ServerContractFilters = {
  signingState: SigningStateQuickFilter;
  contractType: string;
  accountManagerEmail: string;
  search: string;
  volet: string;
};

const SEGMENT_QUICK_FILTER_VALUES: Record<
  ContractSegment,
  { contractType: 'main' | 'amendment'; volet: 'SST' | 'PDS' }
> = {
  contractSst: { contractType: 'main', volet: 'SST' },
  contractPds: { contractType: 'main', volet: 'PDS' },
  amendmentSst: { contractType: 'amendment', volet: 'SST' },
  amendmentPds: { contractType: 'amendment', volet: 'PDS' },
};

export type TableLegalContractsType = {
  missionsContracts: ContractsByMissionGQL[];
  loading?: boolean;
  /**
   * Controlled signing quick-filter (`''` = all). Pair with `onSigningStateFilterChange`
   * (e.g. banner « Voir tout » → `'toSign'`).
   */
  signingStateFilter?: SigningStateQuickFilter;
  onSigningStateFilterChange?: (value: SigningStateQuickFilter) => void;
  segmentFilter?: ContractSegment | '';
  onSegmentFilterChange?: (segment: ContractSegment | '') => void;
  /** Server-provided AM options; omit to hide the AM filter. */
  accountManagerOptions?: FilterOption[];
  /** When set, enables server filter mode (parent owns the query). */
  onServerFiltersChange?: (filters: ServerContractFilters) => void;
  /** Parent paginates by mission: disable the grid's per-row pager. */
  serverPaginated?: boolean;
  noRowsLabel: string;
};

const TableLegalContracts = ({
  missionsContracts,
  loading = false,
  signingStateFilter,
  onSigningStateFilterChange,
  segmentFilter,
  onSegmentFilterChange,
  accountManagerOptions = [],
  onServerFiltersChange,
  serverPaginated = false,
  noRowsLabel,
}: TableLegalContractsType) => {
  const { t } = useTranslation(['legalContracts', 'mission']);
  const [token, setToken] = useState<string | undefined>();
  const auth = useAuthStore((state) => state.auth);
  const serverMode = onServerFiltersChange !== undefined;
  // Server mode always stays grouped: result count must not flip layout or hide filters (e.g. AM).
  const groupedByMission = serverMode || missionsContracts.length !== 1;
  const isSigningStateControlled = onSigningStateFilterChange !== undefined;
  const isSegmentControlled = onSegmentFilterChange !== undefined;

  useEffect(() => {
    if (auth?.getAccessTokenSilently) {
      auth.getAccessTokenSilently().then((token) => {
        setToken(token);
      });
    }
  }, [auth]);

  const [fileViewerPath, setFileViewerPath] = useState<string | undefined>(undefined);
  const [uploadingFile, setUploadingFile] = useState<boolean>(false);

  const isAdmin = auth?.user?.roles?.includes(Role.Admin) || false;
  const isSystem = auth?.user?.roles?.includes(Role.System) || false;
  const shouldApplyDefaultFilter = isAdmin && !isSystem;
  const defaultAccountManagerEmail = shouldApplyDefaultFilter ? auth?.user?.email || undefined : undefined;

  const [quickFilterValues, setQuickFilterValues] = useState<Record<string, string>>({});
  const hasUserChangedFiltersRef = useRef(false);
  /** Distinguishes grid chip changes from parent-driven CTA updates. */
  const signingStateChangeFromGridRef = useRef(false);

  const effectiveQuickFilterValues = useMemo(() => {
    let values = quickFilterValues;
    if (isSigningStateControlled) {
      values = { ...values, signingState: signingStateFilter ?? '' };
    }
    if (isSegmentControlled && segmentFilter) {
      const { contractType, volet } = SEGMENT_QUICK_FILTER_VALUES[segmentFilter];
      values = { ...values, signingState: 'toSign', contractType, volet };
    }
    return values;
  }, [quickFilterValues, isSigningStateControlled, signingStateFilter, isSegmentControlled, segmentFilter]);

  const activeSigningState: SigningStateFilter =
    effectiveQuickFilterValues.signingState === 'toSign' || effectiveQuickFilterValues.signingState === 'signed'
      ? effectiveQuickFilterValues.signingState
      : '';

  const activeVolet: '' | 'SST' | 'PDS' =
    effectiveQuickFilterValues.volet === 'SST' || effectiveQuickFilterValues.volet === 'PDS'
      ? effectiveQuickFilterValues.volet
      : '';

  const handleQuickFilterValuesChange = (values: Record<string, string>) => {
    hasUserChangedFiltersRef.current = true;

    let segmentStillActive = false;
    if (isSegmentControlled && segmentFilter) {
      const expected = SEGMENT_QUICK_FILTER_VALUES[segmentFilter];
      segmentStillActive =
        values.signingState === 'toSign' &&
        values.contractType === expected.contractType &&
        values.volet === expected.volet;
      if (!segmentStillActive) {
        onSegmentFilterChange('');
      }
    }

    if (isSigningStateControlled) {
      const nextSigningState: SigningStateQuickFilter =
        values.signingState === 'toSign' || values.signingState === 'signed' ? values.signingState : '';
      // Only mark grid-origin when signingState actually changed (avoids swallowing the next CTA).
      if (nextSigningState !== (signingStateFilter ?? '')) {
        signingStateChangeFromGridRef.current = true;
        onSigningStateFilterChange(nextSigningState);
      }
    }

    const rest = { ...values };
    if (isSigningStateControlled || segmentStillActive) delete rest.signingState;
    if (segmentStillActive) {
      delete rest.contractType;
      delete rest.volet;
    }
    setQuickFilterValues(rest);
  };

  /**
   * Restores the pre-existing "land scoped to my own missions" default
   * (FR-014) for a non-system admin. The old default *status* filter (hide
   * `done`/`cancelled`) is intentionally not reproduced: it applied per
   * document row, but a grid row now carries both SST and PDS volets, and
   * mapping it onto the existing binary "à signer / signés" quick filter
   * would silently hide `refused`-only lines by default — worse than
   * showing everything. See specs/003-contracts-mission-view/tasks.md T030.
   */
  useEffect(() => {
    if (!groupedByMission) return;
    if (!defaultAccountManagerEmail) return;
    if (hasUserChangedFiltersRef.current) return;
    setQuickFilterValues((prev) =>
      prev.accountManagerEmail ? prev : { ...prev, accountManagerEmail: defaultAccountManagerEmail },
    );
  }, [defaultAccountManagerEmail, groupedByMission]);

  /**
   * Banner « Voir tout » sets signingState to `'toSign'` from the parent: clear default AM/type
   * scoping so all to-sign docs counted by the global banner are visible. Grid-origin changes skip this.
   */
  useEffect(() => {
    if (!isSigningStateControlled) return;
    if (signingStateChangeFromGridRef.current) {
      signingStateChangeFromGridRef.current = false;
      return;
    }
    if (signingStateFilter !== 'toSign') return;
    hasUserChangedFiltersRef.current = true;
    setQuickFilterValues((prev) => {
      if (!prev.accountManagerEmail && !prev.contractType) return prev;
      const { accountManagerEmail: _am, contractType: _ct, ...rest } = prev;
      return rest;
    });
  }, [isSigningStateControlled, signingStateFilter]);

  const blocks: MissionContractBlock[] = useMemo(
    () => buildMissionContractGroups(missionsContracts, t),
    [missionsContracts, t],
  );

  const blocksByMissionId = useMemo(() => new Map(blocks.map((block) => [block.missionId, block])), [blocks]);

  const rows: MissionContractGridRow[] = useMemo(() => flattenMissionContractGroups(blocks), [blocks]);

  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (!onServerFiltersChange) return;
    onServerFiltersChange({
      signingState:
        effectiveQuickFilterValues.signingState === 'toSign' || effectiveQuickFilterValues.signingState === 'signed'
          ? effectiveQuickFilterValues.signingState
          : '',
      contractType: effectiveQuickFilterValues.contractType ?? '',
      accountManagerEmail: effectiveQuickFilterValues.accountManagerEmail ?? '',
      search: searchText,
      volet: activeVolet,
    });
  }, [onServerFiltersChange, effectiveQuickFilterValues, searchText]);

  const quickFilters: DataGridV2QuickFilter<MissionContractGridRow>[] = useMemo(() => {
    const filters: DataGridV2QuickFilter<MissionContractGridRow>[] = [
      {
        field: 'signingState',
        label: t('signingQuickFilterLabel'),
        display: 'inline',
        allLabel: t('signingQuickFilterAll'),
        options: [
          { value: 'toSign', label: t('signingQuickFilterToSign'), tone: 'warning' },
          { value: 'signed', label: t('signingQuickFilterSigned'), tone: 'success' },
        ],
        predicate: (row, value) => {
          const filter: SigningStateFilter = value === 'toSign' || value === 'signed' ? value : '';
          return isVoletVisibleUnderSigningFilter(row.sst, filter) || isVoletVisibleUnderSigningFilter(row.pds, filter);
        },
      },
      {
        field: 'contractType',
        label: t('typeQuickFilterLabel'),
        options: [
          { value: 'main', label: t('typeQuickFilterMainContract') },
          { value: 'amendment', label: t('typeQuickFilterAmendment') },
        ],
        predicate: (row, value) => (value === 'main' ? row.stepOrder === 0 : row.stepOrder > 0),
      },
      {
        field: 'volet',
        label: t('voletQuickFilterLabel'),
        options: [
          { value: 'SST', label: t('voletType.SST') },
          { value: 'PDS', label: t('voletType.PDS') },
        ],
        predicate: (row, value) => (value === 'SST' ? row.sst !== null : row.pds !== null),
      },
    ];
    if (groupedByMission && accountManagerOptions.length > 0) {
      filters.push({
        field: 'accountManagerEmail',
        label: t('accountManagerQuickFilterLabel'),
        options: accountManagerOptions,
      });
    }
    return filters;
  }, [accountManagerOptions, groupedByMission, t]);

  const columns: DataGridV2Column<MissionContractGridRow>[] = useMemo(() => {
    const dataColumns: DataGridV2Column<MissionContractGridRow>[] = [
      {
        field: 'step',
        header: t('step'),
        width: 180,
        minWidth: 140,
        sortable: false,
        filterable: true,
        groupable: false,
        // When missions are grouped, `step` is shown as the leaf value of the
        // grouping column (`leafField`) — keep the column for mono-mission view
        // and as the source field for leaves, but hide it in the grouped layout.
        hideable: true,
        variant: 'custom',
        render: (row) => row.step,
      },
      {
        field: 'sst',
        header: t('sstVolet'),
        minWidth: 280,
        flex: 1.5,
        sortable: false,
        filterable: false,
        groupable: false,
        variant: 'custom',
        render: (row) =>
          isVoletVisibleUnderSigningFilter(row.sst, activeSigningState) && activeVolet !== 'PDS' ? (
            <DocumentVoletCard
              volet={row.sst}
              voletKind={TemplateContract.SST}
              missionRecordId={row.missionId}
              resolvedSigner={row.sstResolvedSigner}
              onClickPreview={setFileViewerPath}
              onClickDownload={(file) => {
                void downloadSignedFile(file, t, token);
              }}
              setUploadingFile={setUploadingFile}
            />
          ) : null,
      },
      {
        field: 'pds',
        header: t('pdsVolet'),
        minWidth: 280,
        flex: 1.5,
        sortable: false,
        filterable: false,
        groupable: false,
        variant: 'custom',
        render: (row) =>
          isVoletVisibleUnderSigningFilter(row.pds, activeSigningState) && activeVolet !== 'SST' ? (
            <DocumentVoletCard
              volet={row.pds}
              voletKind={TemplateContract.PDS}
              missionRecordId={row.missionId}
              resolvedSigner={row.pdsResolvedSigner}
              onClickPreview={setFileViewerPath}
              onClickDownload={(file) => {
                void downloadSignedFile(file, t, token);
              }}
              setUploadingFile={setUploadingFile}
            />
          ) : null,
      },
    ];

    if (!groupedByMission) return dataColumns;

    return [
      {
        field: 'missionId',
        header: t('label', { ns: 'mission' }),
        groupable: true,
        sortable: false,
        filterable: false,
        hideable: false,
        variant: 'text',
      },
      ...dataColumns,
    ];
  }, [activeSigningState, activeVolet, groupedByMission, t, token]);

  return (
    <>
      {uploadingFile && <LoaderPending text={t('uploading')} />}
      <Modal
        open={!!fileViewerPath}
        onClose={() => setFileViewerPath(undefined)}
        additionnalCSS={{
          width: '1250px',
          padding: '32px',
          border: 'none',
          borderRadius: '20px',
          height: '98%',
        }}
      >
        <FileViewer width="1200px" url={fileViewerPath as string} accessToken={token} mimeType="application/pdf" />
      </Modal>
      <DataGridV2<MissionContractGridRow>
        uniqueId={groupedByMission ? 'table-contracts-global-v2' : 'table-contracts-mission-v2'}
        rows={rows}
        columns={columns}
        getRowId={(row) => row.rowId}
        loading={loading}
        loadingVariant="bar"
        noRowsLabel={noRowsLabel}
        zebra={false}
        search
        searchPlaceholder={t('searchPlaceholder')}
        searchFields={[
          'missionLabel',
          'bloomerFullName',
          'clientSocietyName',
          'bloomerSocietyName',
          'sstSignerFullName',
          'pdsSignerFullName',
          'step',
        ]}
        quickFilters={quickFilters}
        quickFilterValues={effectiveQuickFilterValues}
        onQuickFilterValuesChange={handleQuickFilterValuesChange}
        {...(serverMode ? { onServerSearch: setSearchText } : {})}
        pagination={!serverPaginated}
        hideFooter={serverPaginated}
        pageSize={20}
        sortingMode="client"
        filterMode={serverMode ? 'server' : 'client'}
        defaultGroupingExpansionDepth={-1}
        rowGroupingModel={groupedByMission ? ['missionId'] : []}
        initialState={
          groupedByMission
            ? {
                columns: {
                  // `missionId` is only the grouping key; `step` is rendered as
                  // the leaf value inside the grouping column (see leafField).
                  columnVisibilityModel: { missionId: false, step: false },
                },
              }
            : undefined
        }
        muiProps={{
          // Grow with content so the page scrollbar is used — otherwise the
          // virtual scroller traps the wheel and page scroll feels stuck.
          autoHeight: true,
          disablePivoting: true,
          ...(groupedByMission
            ? {
                groupingColDef: {
                  headerName: t('step'),
                  // Narrow for leaf rows ("Contrat principal" / "Avenant N"); group rows
                  // span the full row width via colSpan so mission headers stay readable.
                  minWidth: 180,
                  width: 200,
                  flex: 0.4,
                  leafField: 'step',
                  colSpan: resolveGroupedContractsColSpan,
                  renderCell: (params) => {
                    const { rowNode } = params;
                    if (!rowNode) return null;
                    if (rowNode.type === 'group') {
                      const block = blocksByMissionId.get(String(rowNode.groupingKey));
                      if (!block) return null;
                      return (
                        <MissionGroupCell
                          block={block}
                          rowId={params.id}
                          childrenExpanded={!!rowNode.childrenExpanded}
                        />
                      );
                    }
                    if (rowNode.type === 'leaf') {
                      return <StepLeaf>{String(params.row?.step ?? params.value ?? '')}</StepLeaf>;
                    }
                    return null;
                  },
                },
              }
            : {}),
        }}
      />
    </>
  );
};

const StepLeaf = styled('span')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.neutral[80],
  whiteSpace: 'nowrap',
}));

export default TableLegalContracts;
